import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import {
  MapContainer,
  TileLayer,
  Marker,
  useMap,
  useMapEvents,
} from "react-leaflet";
import { useNavigate } from "react-router-dom";
import HeaderMain from "./Components/HeaderMain";
import ImageModal from "../common/FullScreenImageModal";
import "./ManageUser.css";
import ProfilePic from "./images/dp.png";
import axios from "axios";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import ConfirmationModal from "../common/ConfirmationModal";
import TransferModal from "../common/TransferModal";
import { useAuth } from "../context/AuthContext";

import {
  validatePassword,
  validatePasswordMatch,
} from "../utils/passwordValidation";

const API_URL = process.env.REACT_APP_BASE_URL;

const calculateAge = (dob) => {
  const today = new Date();
  const birthDate = new Date(dob);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDifference = today.getMonth() - birthDate.getMonth();
  if (
    monthDifference < 0 ||
    (monthDifference === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }
  return age;
};

const SetViewOnClick = ({ coords, shouldPan }) => {
  const map = useMap();
  useEffect(() => {
    if (shouldPan && coords[0] !== 0 && coords[1] !== 0) {
      map.setView(coords, 13);
    }
  }, [coords, map, shouldPan]);
  return null;
};

const MapEvents = ({ onMapClick }) => {
  useMapEvents({
    click: onMapClick,
  });
  return null;
};

const ManageUser = () => {
  const [userId, setUserId] = useState(null);
  const [profileImage, setProfileImage] = useState(null);
  const [userEmail, setUserEmail] = useState("");
  const [userName, setUserName] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [imageBlob, setImageBlob] = useState(null);
  const [isPersonalEditable, setIsPersonalEditable] = useState(false);
  const [isAddressEditable, setIsAddressEditable] = useState(false);
  const [isPersonal, setIsPersonal] = useState(false);
  const [isAddress, setIsAddress] = useState(false);
  const [mapCenter, setMapCenter] = useState([33.6844, 73.0479]); // Default to Islamabad
  const [marker, setMarker] = useState(null);
  const [shouldPan, setShouldPan] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const mapRef = useRef(null);
  const navigate = useNavigate();

  // Add these state variables inside your ManageUser component
  const [sportsOptions, setSportsOptions] = useState([]);
  const [selectedSports, setSelectedSports] = useState([]);
  const [isInterestsEditable, setIsInterestsEditable] = useState(false);
  const [isInterests, setIsInterests] = useState(false);
  const animatedComponents = makeAnimated();

  // Transfer modal states
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [transferAmount, setTransferAmount] = useState("");
  const [transferDescription, setTransferDescription] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [userSuggestions, setUserSuggestions] = useState([]);
  const [userBalance, setUserBalance] = useState(0);
  const [userWalletNumber, setUserWalletNumber] = useState("");
  const [isTransferring, setIsTransferring] = useState(false);

  // Validation and error states
  const [errors, setErrors] = useState({});
  const [isSubmittingPersonal, setIsSubmittingPersonal] = useState(false);
  const [isSubmittingAddress, setIsSubmittingAddress] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);

  // Transaction history states
  const [isTransactions, setIsTransactions] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showTransactionModal, setShowTransactionModal] = useState(false);

  // Password change states
  const [isPasswordSection, setIsPasswordSection] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Image modal states
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  const [parentInfo, setParentInfo] = useState({
    user_name: "",
    first_name: "",
    middle_name: "",
    last_name: "",
    contact: "",
    dob: "",
    house_number: "",
    street_number: "",
    area: "",
    city: "",
    country: "",
    coordinates: "",
    emergency_contact: "",
    picture: null,
    age: "",
    medical_condition: "",
    is_profile_private: false,
  });

  const { user, refreshUserInterests } = useAuth();

  // Validation functions
  const validateRequiredField = (value, fieldName) => {
    if (!value || value.trim() === "") {
      return `${fieldName} is required`;
    }
    return "";
  };

  const validatePersonalInfo = () => {
    const newErrors = {};

    // Validate required fields
    newErrors.first_name = validateRequiredField(
      parentInfo.first_name,
      "First name"
    );
    newErrors.last_name = validateRequiredField(
      parentInfo.last_name,
      "Last name"
    );
    newErrors.contact = validateRequiredField(parentInfo.contact, "Contact");
    newErrors.dob = validateRequiredField(parentInfo.dob, "Date of birth");

    // Validate contact format (phone number) - only if contact is provided and not already caught by required validation
    if (
      parentInfo.contact &&
      !newErrors.contact &&
      !/^0\d{3}-\d{7}$/.test(parentInfo.contact)
    ) {
      newErrors.contact =
        "Please enter a valid phone number in format: 0xxx-xxxxxxx";
    }

    // Validate emergency contact if provided
    if (
      parentInfo.emergency_contact &&
      !/^0\d{3}-\d{7}$/.test(parentInfo.emergency_contact)
    ) {
      newErrors.emergency_contact =
        "Please enter a valid emergency contact number in format: 0xxx-xxxxxxx";
    }

    // Validate date of birth format and age
    if (parentInfo.dob && !newErrors.dob) {
      const dobDate = new Date(parentInfo.dob);
      const today = new Date();

      // Check if date is valid
      if (isNaN(dobDate.getTime())) {
        newErrors.dob = "Please enter a valid date";
      }
      // Check if date is not in the future
      else if (dobDate > today) {
        newErrors.dob = "Date of birth cannot be in the future";
      }
      // Check if person is not too old (e.g., over 150 years)
      else if (calculateAge(parentInfo.dob) > 100) {
        newErrors.dob = "Please enter a valid date of birth";
      }
      // Check if person is not too young (e.g., under 5 years)
      else if (calculateAge(parentInfo.dob) < 5) {
        newErrors.dob = "You must be at least 5 years old";
      }
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error !== "");
  };

  const validateAddressInfo = () => {
    const newErrors = {};

    // Validate required fields
    newErrors.city = validateRequiredField(parentInfo.city, "City");
    newErrors.country = validateRequiredField(parentInfo.country, "Country");
    newErrors.area = validateRequiredField(parentInfo.area, "Area");
    newErrors.street_number = validateRequiredField(
      parentInfo.street_number,
      "Street number"
    );
    newErrors.coordinates = validateRequiredField(
      parentInfo.coordinates,
      "Coordinates"
    );

    // Validate coordinates format (latitude, longitude)
    if (
      parentInfo.coordinates &&
      !/^-?\d+\.\d+,\s*-?\d+\.\d+$/.test(parentInfo.coordinates)
    ) {
      newErrors.coordinates =
        "Please enter valid coordinates (latitude, longitude)";
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error !== "");
  };

  // Add this useEffect to fetch sports options
  useEffect(() => {
    const fetchSportsOptions = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/common/get-sports`);

        // Filter out "General" sport for user selection
        const filteredSports = response.data.filter(
          (sport) => sport.value !== "General"
        );
        const formattedOptions = filteredSports.map((sport) => ({
          value: sport.id,
          label: sport.value,
        }));

        setSportsOptions(formattedOptions);
      } catch (error) {
        console.error("Error fetching sports options:", error);
      }
    };

    fetchSportsOptions();
  }, []);

  // Add this useEffect to fetch user's interests
  useEffect(() => {
    const fetchUserInterests = async () => {
      if (!userId) return;

      try {
        const response = await axios.get(
          `${API_URL}/api/common/get-user-interests/${userId}`,
          { withCredentials: true }
        );

        // Format the interests for react-select
        const formattedInterests = response.data.map((sport) => ({
          value: sport.id,
          label: sport.name,
        }));

        setSelectedSports(formattedInterests);
      } catch (error) {
        console.error("Error fetching user interests:", error);
      }
    };

    fetchUserInterests();
  }, [userId]);

  // Fetch user balance when component mounts and userId is available
  useEffect(() => {
    if (userId) {
      fetchUserBalance();
    }
  }, [userId]);

  // Fetch user profile when auth user is available
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        if (!user?.uid) return;
        setUserId(user.uid);

        const response = await axios.get(
          `${API_URL}/api/common/get-user-profile/${user.uid}`,
          { withCredentials: true }
        );
        const userData = response.data;

        // Update state with fetched data
        setUserName(`${userData.first_name} ${userData.last_name}`);
        setUserEmail(userData.email || "user@example.com");

        // Set profile picture if available
        if (userData.picture) {
          // Convert the blob data to a base64 string
          if (
            typeof userData.picture === "object" &&
            userData.picture !== null
          ) {
            // Handle blob data
            const blob = new Blob([userData.picture.data], {
              type: "image/jpeg",
            });
            setImageBlob(blob);
            const reader = new FileReader();
            reader.onload = (e) => {
              setProfileImage(e.target.result);
            };
            reader.readAsDataURL(blob);
          } else if (typeof userData.picture === "string") {
            // Handle if it's already a base64 string
            setProfileImage(`data:image/jpeg;base64,${userData.picture}`);
          }
        }

        // Update parent info state
        setParentInfo({
          user_name: userData.user_name || "",
          first_name: userData.first_name || "",
          middle_name: userData.middle_name || "",
          last_name: userData.last_name || "",
          contact: userData.contact || "",
          dob: userData.dob
            ? new Date(userData.dob).toISOString().split("T")[0]
            : "",
          emergency_contact: userData.emergency_contact || "",
          age: userData.age || "",
          medical_condition: userData.medical_condition || "",
          is_profile_private: userData.is_profile_private || false,
          // Address fields
          house_number: userData.address?.house_no || "",
          street_number: userData.address?.street || "",
          area: userData.address?.area || "",
          city: userData.address?.city || "",
          country: userData.address?.country || "",
          coordinates: userData.address?.coordinates || "",
        });

        // Set map marker if coordinates are available
        if (userData.address?.coordinates) {
          const [lat, lng] = userData.address.coordinates
            .split(",")
            .map((coord) => parseFloat(coord.trim()));
          if (!isNaN(lat) && !isNaN(lng)) {
            setMapCenter([lat, lng]);
            setMarker([lat, lng]);
            setShouldPan(true);
          }
        }

        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching user profile:", error);
        setIsLoading(false);
      }
    };

    fetchUserProfile();
  }, [user]);

  // Add these handler functions
  const handleInterestsInfo = () => {
    setIsInterests(!isInterests);
  };

  const toggleInterestsEdit = () => {
    if (isInterestsEditable) {
      // Save changes
      handleSaveInterests();
    }
    setIsInterestsEditable(!isInterestsEditable);
  };

  const handleSaveInterests = async () => {
    // Validate that at least one sport is selected
    if (!selectedSports || selectedSports.length === 0) {
      toast.error("Please select at least one sport interest before saving.");
      return;
    }

    try {
      const sportNames = selectedSports.map((sport) => sport.label);

      await axios.put(
        `${API_URL}/api/common/update-user-interests/${userId}`,
        {
          interests: sportNames,
        },
        { withCredentials: true }
      );

      // Sync context so other parts (e.g., dashboard) get updated interests
      await refreshUserInterests();

      toast.success("Sports interests updated successfully!");

      // Exit edit mode after successful save
      setIsInterestsEditable(false);
    } catch (error) {
      console.error("Error updating sports interests:", error);
      toast.error("Failed to update sports interests. Please try again.");
    }
  };

  const handleSportsChange = (selectedOptions) => {
    setSelectedSports(selectedOptions || []);
  };

  // Password change handlers
  const handlePasswordSectionToggle = () => {
    setIsPasswordSection(!isPasswordSection);
    // Reset form when closing
    if (isPasswordSection) {
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setPasswordErrors({});
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field when user starts typing
    if (passwordErrors[name]) {
      setPasswordErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validatePasswordForm = () => {
    const newErrors = {};

    if (!passwordData.currentPassword) {
      newErrors.currentPassword = "Current password is required";
    }

    // Validate new password using the utility function
    const passwordError = validatePassword(passwordData.newPassword);
    if (passwordError) {
      newErrors.newPassword = passwordError;
    }

    // Check if new password is different from current password
    if (
      passwordData.currentPassword &&
      passwordData.newPassword &&
      passwordData.currentPassword === passwordData.newPassword
    ) {
      newErrors.newPassword =
        "New password must be different from current password";
    }

    // Validate password match
    const matchError = validatePasswordMatch(
      passwordData.newPassword,
      passwordData.confirmPassword
    );
    if (matchError) {
      newErrors.confirmPassword = matchError;
    }

    setPasswordErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePasswordSubmit = async () => {
    if (!validatePasswordForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    setIsChangingPassword(true);

    try {
      await axios.put(
        `${API_URL}/api/common/change-user-password/${userId}`,
        {
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword,
        },
        { withCredentials: true }
      );

      toast.success("Password changed successfully!");

      // Reset form
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setPasswordErrors({});
    } catch (error) {
      console.error("Error changing password:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to change password. Please try again.");
      }
    } finally {
      setIsChangingPassword(false);
    }
  };

  // Transfer functionality
  const fetchUserBalance = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/accounting/balance/${userId}`,
        { withCredentials: true }
      );
      setUserBalance(response.data.balance);
      if (response.data.wallet_number) {
        setUserWalletNumber(response.data.wallet_number);
      }
    } catch (error) {
      console.error("Error fetching balance:", error);
    }
  };

  const searchUsers = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setUserSuggestions([]);
      return;
    }

    try {
      const response = await axios.get(
        `${API_URL}/api/accounting/search-users?searchTerm=${searchTerm}&currentUserId=${userId}`,
        { withCredentials: true }
      );
      setUserSuggestions(response.data);
    } catch (error) {
      console.error("Error searching users:", error);
      setUserSuggestions([]);
    }
  };

  const handleUserSearchChange = (e) => {
    const value = e.target.value;
    setUserSearchTerm(value);
    searchUsers(value);
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setUserSearchTerm(user.full_name);
    setUserSuggestions([]);
  };

  const handleTransferSubmit = () => {
    // All validation is now handled in the TransferModal component
    setShowConfirmModal(true);
  };

  const handleConfirmTransfer = async () => {
    setIsTransferring(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/accounting/transfer`,
        {
          senderUserId: userId,
          receiverUserId: selectedUser.id,
          amount: parseFloat(transferAmount),
          description: transferDescription,
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        alert("Transfer completed successfully!");
        setUserBalance(response.data.newBalance);
        // Reset form
        setTransferAmount("");
        setTransferDescription("");
        setSelectedUser(null);
        setUserSearchTerm("");
        setShowTransferModal(false);
        setShowConfirmModal(false);
      }
    } catch (error) {
      console.error("Error transferring money:", error);
      alert(error.response?.data?.error || "Transfer failed");
    } finally {
      setIsTransferring(false);
    }
  };

  const handleCancelTransfer = () => {
    setShowConfirmModal(false);
  };

  // Transaction history functionality
  const handleTransactionsInfo = () => {
    setIsTransactions(!isTransactions);
    if (!isTransactions && transactions.length === 0) {
      fetchTransactions();
    }
  };

  const fetchTransactions = async () => {
    setIsLoadingTransactions(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/accounting/transactions/${userId}?limit=10`,
        { withCredentials: true }
      );
      setTransactions(response.data);
    } catch (error) {
      console.error("Error fetching transactions:", error);
      setTransactions([]);
    } finally {
      setIsLoadingTransactions(false);
    }
  };

  const handleViewAllTransactions = () => {
    navigate("/transaction-history");
  };

  const handleTransactionDetails = (transaction) => {
    setSelectedTransaction(transaction);
    setShowTransactionModal(true);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Add this function to validate image size before upload
  const validateImageSize = (file) => {
    const maxSize = 64 * 1024; // 64KB in bytes

    if (file.size > maxSize) {
      toast.error(
        "Image is too large. Maximum size is 64KB. Please choose a smaller image or compress this one."
      );
      return false;
    }
    return true;
  };

  const handleParentInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "dob") {
      const age = calculateAge(value);
      setParentInfo({ ...parentInfo, [name]: value, age });
    } else {
      setParentInfo({ ...parentInfo, [name]: value });
    }
  };

  const handlePersonalInfo = () => {
    setIsPersonal(!isPersonal);
  };

  const handleAddressInfo = () => {
    setIsAddress(!isAddress);
  };

  const handleDashboardClick = () => {
    navigate("/dashboard");
  };

  const handleAddScoreClick = () => {
    navigate("/add-game");
  };

  const handleViewScoreClick = () => {
    navigate("/view-game");
  };

  // Update your handleFileChange function
  const handleFileChange = (e) => {
    const file = e.target.files[0];

    if (file && validateImageSize(file)) {
      setSelectedFile(file);

      // Preview the image
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfileImage(e.target.result);
      };
      reader.readAsDataURL(file);
    } else {
      // Reset the file input
      e.target.value = "";
    }
  };

  const handleUploadPhoto = async () => {
    if (!selectedFile) {
      toast.error("Please select a file to upload");
      return;
    }

    setIsUploadingPhoto(true);

    try {
      const reader = new FileReader();
      reader.readAsDataURL(selectedFile);
      reader.onload = async () => {
        const base64Image = reader.result.split(",")[1]; // Remove data URL prefix

        await axios.put(
          `${API_URL}/api/common/update-user-picture/${userId}`,
          {
            picture: base64Image,
          },
          { withCredentials: true }
        );

        toast.success("Profile picture updated successfully!");
        // Clear the selected file after successful upload
        setSelectedFile(null);
        // Reset the file input
        document.getElementById("profilePicInput").value = "";
      };
    } catch (error) {
      console.error("Error uploading photo:", error);
      toast.error("Failed to upload photo. Please try again.");
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const togglePersonalEdit = () => {
    if (isPersonalEditable) {
      // Save changes
      handleSavePersonalInfo();
    }
    setIsPersonalEditable(!isPersonalEditable);
  };

  const toggleAddressEdit = () => {
    if (isAddressEditable) {
      // Save changes
      handleSaveAddressInfo();
    }
    setIsAddressEditable(!isAddressEditable);
  };

  const handleSavePersonalInfo = async () => {
    // Clear previous errors
    setErrors({});

    // Validate form
    if (!validatePersonalInfo()) {
      // Show toast notification for validation errors
      const errorFields = Object.values(errors).filter((error) => error !== "");
      if (errorFields.length > 0) {
        toast.error(
          `Please fix the following errors: ${errorFields.join(", ")}`
        );
      } else {
        toast.error("Please fill in all required fields correctly.");
      }
      return;
    }

    setIsSubmittingPersonal(true);

    try {
      // Calculate age from DOB to ensure it's up to date
      const calculatedAge = calculateAge(parentInfo.dob);

      const personalData = {
        user_name: parentInfo.user_name,
        first_name: parentInfo.first_name,
        middle_name: parentInfo.middle_name,
        last_name: parentInfo.last_name,
        contact: parentInfo.contact,
        dob: parentInfo.dob,
        age: calculatedAge,
        emergency_contact: parentInfo.emergency_contact,
        medical_condition: parentInfo.medical_condition,
        is_profile_private: parentInfo.is_profile_private,
      };

      await axios.put(
        `${API_URL}/api/common/update-user-personal/${userId}`,
        personalData,
        { withCredentials: true }
      );

      // Update displayed name and age
      setUserName(`${personalData.first_name} ${personalData.last_name}`);
      setParentInfo({
        ...parentInfo,
        age: calculatedAge,
      });

      toast.success("Personal information updated successfully!");
    } catch (error) {
      console.error("Error updating personal info:", error);
      toast.error("Failed to update personal information. Please try again.");
    } finally {
      setIsSubmittingPersonal(false);
    }
  };

  const handleSaveAddressInfo = async () => {
    // Clear previous errors
    setErrors({});

    // Validate form
    if (!validateAddressInfo()) {
      return;
    }

    setIsSubmittingAddress(true);

    try {
      const addressData = {
        country: parentInfo.country,
        city: parentInfo.city,
        area: parentInfo.area,
        street: parentInfo.street_number,
        house_no: parentInfo.house_number,
        coordinates: parentInfo.coordinates,
      };

      await axios.put(
        `${API_URL}/api/common/update-user-address/${userId}`,
        addressData,
        { withCredentials: true }
      );

      toast.success("Address information updated successfully!");
    } catch (error) {
      console.error("Error updating address info:", error);
      toast.error("Failed to update address information. Please try again.");
    } finally {
      setIsSubmittingAddress(false);
    }
  };

  const handleMapClick = (e) => {
    if (!isAddressEditable) return;

    const { lat, lng } = e.latlng;
    setMarker([lat, lng]);
    setParentInfo({
      ...parentInfo,
      coordinates: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
    });
  };

  const handleUpdateMap = () => {
    // This function will update the map based on the address fields
    // We would need to use a geocoding service here, but for simplicity,
    // we'll just update the map if coordinates are manually entered
    if (parentInfo.coordinates) {
      try {
        const [lat, lng] = parentInfo.coordinates
          .split(",")
          .map((coord) => parseFloat(coord.trim()));
        if (!isNaN(lat) && !isNaN(lng)) {
          setMapCenter([lat, lng]);
          setMarker([lat, lng]);
          setShouldPan(true);
        }
      } catch (error) {
        console.error("Invalid coordinates format:", error);
        toast.error("Please enter valid coordinates (latitude, longitude)");
      }
    }
  };

  if (isLoading) {
    return <div>Loading profile...</div>;
  }

  return (
    <div className="App">
      <HeaderMain pageType="user-profile" />
      <main>
        <div className="MpicSection">
          <div className="Mpic-text">
            <img
              src={profileImage || ProfilePic}
              className="Mprofile-pic1"
              alt="profile"
              onClick={() => setIsImageModalOpen(true)}
              style={{ cursor: "pointer" }}
              title="Click to view full size"
            />
            <div className="Mtext">
              <h3>{userName || "John Doe"}</h3>
              <p>{userEmail || "John@doe.com"}</p>
              {userWalletNumber && (
                <p
                  style={{
                    fontSize: "0.9rem",
                    color: "#0071bc",
                    fontWeight: "600",
                    margin: "4px 0 0 0",
                    fontFamily: "monospace",
                    letterSpacing: "0.5px",
                  }}
                >
                  💳 {userWalletNumber}
                </p>
              )}
            </div>
          </div>
          <div className="uploadBtn editBtn flex flex-wrap gap-3 justify-center md:justify-start">
            <input
              type="file"
              id="profilePicInput"
              style={{ display: "none" }}
              accept="image/*"
              onChange={handleFileChange}
            />
            <button
              onClick={() => document.getElementById("profilePicInput").click()}
              className="bg-gradient-to-r from-green-600 to-green-700 text-white px-6 py-3 rounded-xl font-semibold text-base hover:from-green-700 hover:to-green-800 transition-all duration-200 shadow-lg focus:outline-none focus:ring-4 focus:ring-green-300 flex items-center gap-2"
            >
              ✏️ Select Photo
            </button>
            {selectedFile && (
              <button
                onClick={handleUploadPhoto}
                disabled={isUploadingPhoto}
                className={`bg-gradient-to-r from-purple-600 to-purple-700 text-white px-6 py-3 rounded-xl font-semibold text-base hover:from-purple-700 hover:to-purple-800 transition-all duration-200 shadow-lg focus:outline-none focus:ring-4 focus:ring-purple-300 flex items-center gap-2 ${
                  isUploadingPhoto ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {isUploadingPhoto ? "⏳ Uploading..." : "📤 Upload Photo"}
              </button>
            )}
            <button
              onClick={() => setShowTransferModal(true)}
              className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold text-base hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg focus:outline-none focus:ring-4 focus:ring-blue-300 flex items-center gap-2"
            >
              💸 Transfer Balance
            </button>
          </div>
        </div>

        <div className={`MPersonaldetailSection ${isPersonal ? "open" : ""}`}>
          <p
            className="MHeadings1"
            onClick={handlePersonalInfo}
            style={{ textAlign: "center", fontSize: "24px" }}
          >
            <b>Personal Info</b>
          </p>
          <div className="editBtn">
            <button
              onClick={togglePersonalEdit}
              disabled={isSubmittingPersonal}
              className={`px-6 py-3 rounded-xl font-semibold text-base transition-all duration-200 shadow-lg focus:outline-none focus:ring-4 flex items-center gap-2 ${
                isPersonalEditable
                  ? "bg-gradient-to-r from-green-600 to-green-700 text-white hover:from-green-700 hover:to-green-800 focus:ring-green-300"
                  : "bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 focus:ring-blue-300"
              } ${isSubmittingPersonal ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              {isSubmittingPersonal
                ? "⏳ Saving..."
                : isPersonalEditable
                ? "💾 Save"
                : "✏️ Edit"}
            </button>
          </div>

          <form className="personal">
            <div className="r1">
              <div className="c1">
                <p>User Name</p>
                <input
                  type="text"
                  name="user_name"
                  className="textField"
                  value={parentInfo.user_name}
                  onChange={handleParentInputChange}
                  disabled={true}
                  style={{
                    backgroundColor: "#f5f5f5",
                    color: "#666",
                    cursor: "not-allowed",
                  }}
                />
                <p
                  style={{
                    fontSize: "0.8rem",
                    color: "#666",
                    marginTop: "2px",
                    fontStyle: "italic",
                  }}
                >
                  Username cannot be changed
                </p>
              </div>
              <div className="c2">
                <p>
                  First Name <span style={{ color: "red" }}>*</span>
                </p>
                <input
                  type="text"
                  name="first_name"
                  className={`textField ${errors.first_name ? "error" : ""}`}
                  value={parentInfo.first_name}
                  onChange={(e) => {
                    handleParentInputChange(e);
                    if (errors.first_name) {
                      setErrors((prev) => ({ ...prev, first_name: "" }));
                    }
                  }}
                  disabled={!isPersonalEditable}
                  placeholder="Enter first name"
                />
                {errors.first_name && (
                  <p
                    style={{
                      color: "red",
                      fontSize: "0.8rem",
                      marginTop: "4px",
                    }}
                  >
                    {errors.first_name}
                  </p>
                )}
              </div>
            </div>

            <div className="r2">
              <div className="c1">
                <p>
                  Middle Name{" "}
                  <span style={{ color: "#666", fontSize: "0.85em" }}>
                    (Optional)
                  </span>
                </p>
                <input
                  type="text"
                  name="middle_name"
                  className="textField"
                  value={parentInfo.middle_name}
                  onChange={handleParentInputChange}
                  disabled={!isPersonalEditable}
                  placeholder="Enter middle name"
                />
              </div>
              <div className="c2">
                <p>
                  Last Name <span style={{ color: "red" }}>*</span>
                </p>
                <input
                  type="text"
                  name="last_name"
                  className={`textField ${errors.last_name ? "error" : ""}`}
                  value={parentInfo.last_name}
                  onChange={(e) => {
                    handleParentInputChange(e);
                    if (errors.last_name) {
                      setErrors((prev) => ({ ...prev, last_name: "" }));
                    }
                  }}
                  disabled={!isPersonalEditable}
                  placeholder="Enter last name"
                />
                {errors.last_name && (
                  <p
                    style={{
                      color: "red",
                      fontSize: "0.8rem",
                      marginTop: "4px",
                    }}
                  >
                    {errors.last_name}
                  </p>
                )}
              </div>
            </div>
            <div className="r1">
              <div className="c1">
                <p>
                  Contact <span style={{ color: "red" }}>*</span>
                </p>
                <input
                  type="tel"
                  name="contact"
                  className={`textField ${errors.contact ? "error" : ""}`}
                  value={parentInfo.contact}
                  onChange={(e) => {
                    handleParentInputChange(e);
                    if (errors.contact) {
                      setErrors((prev) => ({ ...prev, contact: "" }));
                    }
                  }}
                  disabled={!isPersonalEditable}
                  placeholder="03XXX-XXXXXXX"
                />
                {errors.contact && (
                  <p
                    style={{
                      color: "red",
                      fontSize: "0.8rem",
                      marginTop: "4px",
                    }}
                  >
                    {errors.contact}
                  </p>
                )}
              </div>
              <div className="c2">
                <p>
                  Emergency Contact{" "}
                  <span style={{ color: "#666", fontSize: "0.85em" }}>
                    (Optional)
                  </span>
                </p>
                <input
                  type="tel"
                  name="emergency_contact"
                  className={`textField ${
                    errors.emergency_contact ? "error" : ""
                  }`}
                  value={parentInfo.emergency_contact}
                  onChange={(e) => {
                    handleParentInputChange(e);
                    if (errors.emergency_contact) {
                      setErrors((prev) => ({ ...prev, emergency_contact: "" }));
                    }
                  }}
                  disabled={!isPersonalEditable}
                  placeholder="0xxx-xxxxxxx"
                />
                {errors.emergency_contact && (
                  <p
                    style={{
                      color: "red",
                      fontSize: "0.8rem",
                      marginTop: "4px",
                    }}
                  >
                    {errors.emergency_contact}
                  </p>
                )}
              </div>
            </div>
            <div className="r2">
              <div className="c1">
                <p>
                  Medical Condition{" "}
                  <span style={{ color: "#666", fontSize: "0.85em" }}>
                    (Optional)
                  </span>
                </p>
                <input
                  type="text"
                  name="medical_condition"
                  className="textField"
                  value={parentInfo.medical_condition}
                  onChange={handleParentInputChange}
                  disabled={!isPersonalEditable}
                  placeholder="Enter medical condition (if any)"
                />
              </div>
              <div className="c2">
                <p>
                  Date Of Birth <span style={{ color: "red" }}>*</span>
                </p>
                <input
                  type="date"
                  name="dob"
                  className={`textField ${errors.dob ? "error" : ""}`}
                  value={parentInfo.dob}
                  onChange={(e) => {
                    handleParentInputChange(e);
                    if (errors.dob) {
                      setErrors((prev) => ({ ...prev, dob: "" }));
                    }
                  }}
                  disabled={!isPersonalEditable}
                  required
                />
                {errors.dob && (
                  <p
                    style={{
                      color: "red",
                      fontSize: "0.8rem",
                      marginTop: "4px",
                    }}
                  >
                    {errors.dob}
                  </p>
                )}
                {isPersonalEditable && (
                  <p
                    style={{
                      fontSize: "0.8rem",
                      color: "#666",
                      marginTop: "4px",
                    }}
                  >
                    Required field. Age will be calculated automatically.
                  </p>
                )}
              </div>
            </div>
            <div className="r3">
              <div className="c1">
                <p>Profile Privacy</p>
                <div className="privacy-options">
                  <div className="opt">
                    <input
                      type="radio"
                      name="is_profile_private"
                      value={false}
                      checked={!parentInfo.is_profile_private}
                      onChange={() =>
                        setParentInfo({
                          ...parentInfo,
                          is_profile_private: false,
                        })
                      }
                      disabled={!isPersonalEditable}
                    />
                    <p>Public Profile</p>
                  </div>
                  <div className="opt">
                    <input
                      type="radio"
                      name="is_profile_private"
                      value={true}
                      checked={parentInfo.is_profile_private}
                      onChange={() =>
                        setParentInfo({
                          ...parentInfo,
                          is_profile_private: true,
                        })
                      }
                      disabled={!isPersonalEditable}
                    />
                    <p>Private Profile</p>
                  </div>
                </div>
                {isPersonalEditable && (
                  <p
                    style={{
                      fontSize: "0.8rem",
                      color: "#666",
                      marginTop: "4px",
                    }}
                  >
                    Public: Others can see your profile without personal info.
                    Private: Only you can see your profile.
                  </p>
                )}
              </div>
            </div>
          </form>
        </div>

        {/* Transaction History Section */}
        <div
          className={`MContactdetailSection ${isTransactions ? "open" : ""}`}
        >
          <p
            className="MHeadings1"
            onClick={handleTransactionsInfo}
            style={{ textAlign: "center", fontSize: "24px" }}
          >
            <b>Transaction History</b>
          </p>

          {isTransactions && (
            <div style={{ padding: "20px 0" }}>
              <div className="balance-info-section">
                <div className="current-balance">
                  <span className="balance-label-section">
                    Current Balance:
                  </span>
                  <span className="balance-amount-section">
                    ${userBalance.toFixed(2)}
                  </span>
                </div>
                <button
                  onClick={fetchTransactions}
                  className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white px-6 py-3 rounded-xl font-semibold text-base hover:from-indigo-700 hover:to-indigo-800 transition-all duration-200 shadow-lg focus:outline-none focus:ring-4 focus:ring-indigo-300 flex items-center gap-2 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed"
                  disabled={isLoadingTransactions}
                >
                  {isLoadingTransactions ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Refreshing...
                    </>
                  ) : (
                    <>🔄 Refresh</>
                  )}
                </button>
              </div>

              {isLoadingTransactions ? (
                <div className="loading-transactions">
                  Loading transactions...
                </div>
              ) : transactions.length === 0 ? (
                <div className="no-transactions">No transactions found</div>
              ) : (
                <div className="transactions-simple-container">
                  <div className="transactions-simple-list">
                    {transactions.map((transaction) => (
                      <div
                        key={transaction.id}
                        className={`transaction-simple-item ${transaction.type}`}
                      >
                        <div className="transaction-simple-main">
                          <div className="transaction-simple-left">
                            <div className="transaction-simple-icon">
                              {transaction.type === "credit" ? "💰" : "💸"}
                            </div>
                            <div className="transaction-simple-info">
                              <div className="transaction-simple-party">
                                {transaction.other_party_type}{" "}
                                {transaction.other_party}
                              </div>
                              {transaction.other_party_wallet &&
                                transaction.other_party_wallet !== "N/A" && (
                                  <div className="transaction-simple-wallet">
                                    💳 {transaction.other_party_wallet}
                                  </div>
                                )}
                              {transaction.transaction_number && (
                                <div className="transaction-simple-tx-number">
                                  📄 {transaction.transaction_number}
                                </div>
                              )}
                              <div className="transaction-simple-date">
                                {formatDate(transaction.created_at)} at{" "}
                                {formatTime(transaction.created_at)}
                              </div>
                              {transaction.description && (
                                <div className="transaction-simple-desc">
                                  {transaction.description.length > 50
                                    ? `${transaction.description.substring(
                                        0,
                                        50
                                      )}...`
                                    : transaction.description}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="transaction-simple-right">
                            <div
                              className={`transaction-simple-amount ${transaction.type}`}
                            >
                              {transaction.type === "credit" ? "+" : "-"}$
                              {transaction.amount.toFixed(2)}
                            </div>
                            <div className="transaction-simple-balance">
                              Balance: ${transaction.new_balance.toFixed(2)}
                            </div>
                            <button
                              onClick={() =>
                                handleTransactionDetails(transaction)
                              }
                              className="transaction-details-btn"
                            >
                              Details
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="transactions-view-all">
                    <button
                      onClick={handleViewAllTransactions}
                      className="view-all-transactions-btn"
                    >
                      📊 View All Transactions
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className={`MContactdetailSection ${isAddress ? "open" : ""}`}>
          <p
            className="MHeadings1"
            onClick={handleAddressInfo}
            style={{ textAlign: "center", fontSize: "24px" }}
          >
            <b>Address Info</b>
          </p>
          <div className="editBtn">
            <button
              onClick={toggleAddressEdit}
              disabled={isSubmittingAddress}
              className={`px-6 py-3 rounded-xl font-semibold text-base transition-all duration-200 shadow-lg focus:outline-none focus:ring-4 flex items-center gap-2 ${
                isAddressEditable
                  ? "bg-gradient-to-r from-green-600 to-green-700 text-white hover:from-green-700 hover:to-green-800 focus:ring-green-300"
                  : "bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 focus:ring-blue-300"
              } ${isSubmittingAddress ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              {isSubmittingAddress
                ? "⏳ Saving..."
                : isAddressEditable
                ? "💾 Save"
                : "✏️ Edit"}
            </button>
          </div>

          <form className="personal">
            <div className="r3">
              <div className="c1">
                <p>
                  House Number{" "}
                  <span style={{ color: "#666", fontSize: "0.85em" }}>
                    (Optional)
                  </span>
                </p>
                <input
                  type="text"
                  name="house_number"
                  className="textField"
                  value={parentInfo.house_number}
                  onChange={handleParentInputChange}
                  disabled={!isAddressEditable}
                  placeholder="Enter house number"
                />
              </div>
              <div className="c2">
                <p>
                  Street Number <span style={{ color: "red" }}>*</span>
                </p>
                <input
                  type="text"
                  name="street_number"
                  className={`textField ${errors.street_number ? "error" : ""}`}
                  value={parentInfo.street_number}
                  onChange={(e) => {
                    handleParentInputChange(e);
                    if (errors.street_number) {
                      setErrors((prev) => ({ ...prev, street_number: "" }));
                    }
                  }}
                  disabled={!isAddressEditable}
                  placeholder="Enter street number"
                />
                {errors.street_number && (
                  <p
                    style={{
                      color: "red",
                      fontSize: "0.8rem",
                      marginTop: "4px",
                    }}
                  >
                    {errors.street_number}
                  </p>
                )}
              </div>
            </div>
            <div className="r4">
              <div className="c1">
                <p>
                  City <span style={{ color: "red" }}>*</span>
                </p>
                <input
                  type="text"
                  name="city"
                  className={`textField ${errors.city ? "error" : ""}`}
                  value={parentInfo.city}
                  onChange={(e) => {
                    handleParentInputChange(e);
                    if (errors.city) {
                      setErrors((prev) => ({ ...prev, city: "" }));
                    }
                  }}
                  disabled={!isAddressEditable}
                  placeholder="Enter city"
                />
                {errors.city && (
                  <p
                    style={{
                      color: "red",
                      fontSize: "0.8rem",
                      marginTop: "4px",
                    }}
                  >
                    {errors.city}
                  </p>
                )}
              </div>
              <div className="c2">
                <p>
                  Country <span style={{ color: "red" }}>*</span>
                </p>
                <input
                  type="text"
                  name="country"
                  className={`textField ${errors.country ? "error" : ""}`}
                  value={parentInfo.country}
                  onChange={(e) => {
                    handleParentInputChange(e);
                    if (errors.country) {
                      setErrors((prev) => ({ ...prev, country: "" }));
                    }
                  }}
                  disabled={!isAddressEditable}
                  placeholder="Enter country"
                />
                {errors.country && (
                  <p
                    style={{
                      color: "red",
                      fontSize: "0.8rem",
                      marginTop: "4px",
                    }}
                  >
                    {errors.country}
                  </p>
                )}
              </div>
            </div>
            <div className="r2">
              <div className="c1">
                <p>
                  Area <span style={{ color: "red" }}>*</span>
                </p>
                <input
                  type="text"
                  name="area"
                  className={`textField ${errors.area ? "error" : ""}`}
                  value={parentInfo.area}
                  onChange={(e) => {
                    handleParentInputChange(e);
                    if (errors.area) {
                      setErrors((prev) => ({ ...prev, area: "" }));
                    }
                  }}
                  disabled={!isAddressEditable}
                  placeholder="Enter area"
                />
                {errors.area && (
                  <p
                    style={{
                      color: "red",
                      fontSize: "0.8rem",
                      marginTop: "4px",
                    }}
                  >
                    {errors.area}
                  </p>
                )}
              </div>
              <div className="c2">
                <p>
                  Coordinates <span style={{ color: "red" }}>*</span>
                </p>
                <input
                  type="text"
                  name="coordinates"
                  className={`textField ${errors.coordinates ? "error" : ""}`}
                  value={parentInfo.coordinates}
                  onChange={(e) => {
                    handleParentInputChange(e);
                    if (errors.coordinates) {
                      setErrors((prev) => ({ ...prev, coordinates: "" }));
                    }
                  }}
                  disabled={!isAddressEditable}
                  placeholder="Click on map or enter coordinates (required)"
                />
                {errors.coordinates && (
                  <p
                    style={{
                      color: "red",
                      fontSize: "0.8rem",
                      marginTop: "4px",
                    }}
                  >
                    {errors.coordinates}
                  </p>
                )}
              </div>
            </div>

            {isAddressEditable && (
              <div className="r1">
                <div className="c1">
                  <button
                    type="button"
                    onClick={handleUpdateMap}
                    style={{ padding: "8px 16px", marginTop: "10px" }}
                  >
                    Update Map
                  </button>
                </div>
              </div>
            )}

            <div className="map-container">
              {isAddressEditable && (
                <div
                  style={{
                    padding: "10px",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "5px",
                    marginBottom: "10px",
                  }}
                >
                  <p style={{ fontSize: "0.95rem", color: "#333", margin: 0 }}>
                    <strong>How to set your location:</strong>
                  </p>
                  <ul
                    style={{
                      fontSize: "0.9rem",
                      margin: "8px 0 0 0",
                      paddingLeft: "20px",
                    }}
                  >
                    <li>Enter your address details in the fields above</li>
                    <li>
                      Click directly on the map to select your exact location
                      (coordinates are required)
                    </li>
                    <li>
                      Or enter coordinates manually and click "Update Map"
                    </li>
                  </ul>
                </div>
              )}
              <MapContainer
                center={mapCenter}
                zoom={13}
                style={{ height: "400px", width: "100%" }}
                ref={mapRef}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                {marker && <Marker position={marker} />}
                <SetViewOnClick coords={mapCenter} shouldPan={shouldPan} />
                <MapEvents onMapClick={handleMapClick} />
              </MapContainer>
              <p
                style={{ marginTop: "5px", fontSize: "0.9rem", color: "#666" }}
              >
                {isAddressEditable
                  ? "Click on the map to set your precise location coordinates (required)"
                  : "Enable edit mode to interact with the map"}
              </p>
            </div>
          </form>
        </div>

        {/* Sports Interests Section */}
        <div className={`MContactdetailSection ${isInterests ? "open" : ""}`}>
          <p
            className="MHeadings1"
            onClick={handleInterestsInfo}
            style={{ textAlign: "center", fontSize: "24px" }}
          >
            <b>Sports Interests</b>
          </p>
          <div className="editBtn">
            <button
              onClick={toggleInterestsEdit}
              className={`px-6 py-3 rounded-xl font-semibold text-base transition-all duration-200 shadow-lg focus:outline-none focus:ring-4 flex items-center gap-2 ${
                isInterestsEditable
                  ? "bg-gradient-to-r from-green-600 to-green-700 text-white hover:from-green-700 hover:to-green-800 focus:ring-green-300"
                  : "bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 focus:ring-blue-300"
              }`}
            >
              {isInterestsEditable ? "💾 Save" : "✏️ Edit"}
            </button>
          </div>

          <form className="personal">
            <div className="r1" style={{ marginBottom: "20px" }}>
              <div className="c1" style={{ width: "100%" }}>
                <p>Select Sports You're Interested In</p>
                <div style={{ marginTop: "10px", zIndex: 100 }}>
                  <Select
                    closeMenuOnSelect={false}
                    components={animatedComponents}
                    isMulti
                    options={sportsOptions}
                    value={selectedSports}
                    onChange={handleSportsChange}
                    isDisabled={!isInterestsEditable}
                    placeholder="Select sports..."
                    noOptionsMessage={() => "No sports available"}
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: "40px",
                        borderRadius: "4px",
                        borderColor: "#ccc",
                      }),
                      menu: (base) => ({
                        ...base,
                        zIndex: 9999,
                        position: "absolute",
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                    }}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </div>
                {selectedSports.length > 0 && (
                  <div style={{ marginTop: "20px", padding: "10px 0" }}>
                    <p
                      style={{
                        fontWeight: "600",
                        marginBottom: "12px",
                        color: "#333",
                      }}
                    >
                      Your selected sports:
                    </p>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "10px",
                      }}
                    >
                      {selectedSports.map((sport) => (
                        <span
                          key={sport.value}
                          style={{
                            background: "#e6f3ff",
                            border: "1px solid #b3d7ff",
                            padding: "6px 12px",
                            borderRadius: "20px",
                            fontSize: "0.9rem",
                            color: "#0071bc",
                            fontFamily: "fSemiBold",
                            display: "inline-block",
                          }}
                        >
                          {sport.label}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {isInterestsEditable && (
                  <p
                    style={{
                      fontSize: "0.85rem",
                      color: "#666",
                      marginTop: "15px",
                      lineHeight: "1.4",
                    }}
                  >
                    You can select multiple sports. These will help us recommend
                    events and activities based on your interests.
                  </p>
                )}
              </div>
            </div>
          </form>
        </div>

        {/* Password Change Section */}
        <div
          className={`MContactdetailSection ${isPasswordSection ? "open" : ""}`}
        >
          <p
            className="MHeadings1"
            onClick={handlePasswordSectionToggle}
            style={{ textAlign: "center", fontSize: "24px" }}
          >
            <b>Change Password</b>
          </p>

          {isPasswordSection && (
            <div style={{ padding: "20px 0" }}>
              <form className="personal">
                <div className="r1">
                  <div className="c1">
                    <p>
                      Current Password <span style={{ color: "red" }}>*</span>
                    </p>
                    <input
                      type="password"
                      name="currentPassword"
                      className={`textField ${
                        passwordErrors.currentPassword ? "error" : ""
                      }`}
                      value={passwordData.currentPassword}
                      onChange={handlePasswordChange}
                      placeholder="Current password"
                      autoComplete="current-password"
                    />
                    {passwordErrors.currentPassword && (
                      <p
                        style={{
                          color: "red",
                          fontSize: "0.8rem",
                          marginTop: "4px",
                        }}
                      >
                        {passwordErrors.currentPassword}
                      </p>
                    )}
                  </div>
                </div>

                <div className="r2">
                  <div className="c1">
                    <p>
                      New Password <span style={{ color: "red" }}>*</span>
                    </p>
                    <input
                      type="password"
                      name="newPassword"
                      className={`textField ${
                        passwordErrors.newPassword ? "error" : ""
                      }`}
                      value={passwordData.newPassword}
                      onChange={handlePasswordChange}
                      placeholder="Enter new password"
                      autoComplete="new-password"
                    />
                    {passwordErrors.newPassword && (
                      <p
                        style={{
                          color: "red",
                          fontSize: "0.8rem",
                          marginTop: "4px",
                        }}
                      >
                        {passwordErrors.newPassword}
                      </p>
                    )}
                  </div>
                  <div className="c2">
                    <p>
                      Confirm New Password{" "}
                      <span style={{ color: "red" }}>*</span>
                    </p>
                    <input
                      type="password"
                      name="confirmPassword"
                      className={`textField ${
                        passwordErrors.confirmPassword ? "error" : ""
                      }`}
                      value={passwordData.confirmPassword}
                      onChange={handlePasswordChange}
                      placeholder="Re-enter new password"
                      autoComplete="new-password"
                    />
                    {passwordErrors.confirmPassword && (
                      <p
                        style={{
                          color: "red",
                          fontSize: "0.8rem",
                          marginTop: "4px",
                        }}
                      >
                        {passwordErrors.confirmPassword}
                      </p>
                    )}
                  </div>
                </div>

                <div className="r1" style={{ marginTop: "20px" }}>
                  <div
                    className="c1"
                    style={{ display: "flex", justifyContent: "center" }}
                  >
                    <button
                      type="button"
                      onClick={handlePasswordSubmit}
                      disabled={isChangingPassword}
                      className={`px-8 py-3 rounded-xl font-semibold text-base transition-all duration-200 shadow-lg focus:outline-none focus:ring-4 flex items-center gap-2 bg-gradient-to-r from-green-600 to-green-700 text-white hover:from-green-700 hover:to-green-800 focus:ring-green-300 ${
                        isChangingPassword
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                    >
                      {isChangingPassword
                        ? "⏳ Changing Password..."
                        : "🔐 Change Password"}
                    </button>
                  </div>
                </div>

                <div className="r1" style={{ marginTop: "15px" }}>
                  <div className="c1">
                    <div
                      style={{
                        padding: "12px",
                        backgroundColor: "#fff3cd",
                        border: "1px solid #ffc107",
                        borderRadius: "8px",
                        fontSize: "0.9rem",
                        color: "#856404",
                      }}
                    >
                      <p style={{ margin: "0 0 8px 0", fontWeight: "600" }}>
                        ⚠️ Password Requirements:
                      </p>
                      <ul style={{ margin: "0", paddingLeft: "20px" }}>
                        <li>Must be at least 8 characters long</li>
                        <li>Must contain at least one number</li>
                        <li>Must contain at least one special character</li>
                        <li>Must be different from your current password</li>
                        <li>New password and confirm password must match</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          )}
        </div>

        <div className={`MContactdetailSection ${isAddress ? "open" : ""}`}>
          <p
            className="MHeadings1"
            onClick={handleDashboardClick}
            style={{ textAlign: "center", fontSize: "24px", cursor: "pointer" }}
          >
            <b>Personal Stats & Workout Dashboard</b>
          </p>
        </div>

        <div className={`MContactdetailSection ${isAddress ? "open" : ""}`}>
          <p
            className="MHeadings1"
            onClick={handleAddScoreClick}
            style={{ textAlign: "center", fontSize: "24px", cursor: "pointer" }}
          >
            <b>Create New Game</b>
          </p>
        </div>

        <div className={`MContactdetailSection ${isAddress ? "open" : ""}`}>
          <p
            className="MHeadings1"
            onClick={handleViewScoreClick}
            style={{ textAlign: "center", fontSize: "24px", cursor: "pointer" }}
          >
            <b>View Your Added Games</b>
          </p>
        </div>
      </main>

      {/* Transfer Modal */}
      <TransferModal
        isOpen={showTransferModal && !showConfirmModal}
        onClose={() => {
          setShowTransferModal(false);
          setTransferAmount("");
          setTransferDescription("");
          setSelectedUser(null);
          setUserSearchTerm("");
        }}
        onSubmit={handleTransferSubmit}
        userBalance={userBalance}
        transferAmount={transferAmount}
        setTransferAmount={setTransferAmount}
        transferDescription={transferDescription}
        setTransferDescription={setTransferDescription}
        selectedUser={selectedUser}
        userSearchTerm={userSearchTerm}
        onUserSearchChange={handleUserSearchChange}
        userSuggestions={userSuggestions}
        onUserSelect={handleUserSelect}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        title="Confirm Transfer"
        message="Are you sure you want to transfer this amount?"
        details={[
          {
            label: "To",
            value: selectedUser?.full_name || "Unknown User",
            extra: selectedUser?.wallet_number
              ? `💳 ${selectedUser.wallet_number}`
              : "",
          },
          { label: "Amount", value: `$${transferAmount}` },
          {
            label: "Description",
            value: transferDescription || "No description",
          },
        ]}
        onConfirm={handleConfirmTransfer}
        onCancel={handleCancelTransfer}
        confirmText="Transfer"
        cancelText="Cancel"
        isLoading={isTransferring}
      />

      {/* Transaction Details Modal */}
      {showTransactionModal && selectedTransaction && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[9999] backdrop-blur-sm">
          <div className="bg-white rounded-xl max-w-lg w-11/12 max-h-[80vh] overflow-y-auto shadow-2xl transform transition-all duration-300 scale-100">
            <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white p-6 rounded-t-xl flex justify-between items-center">
              <h3 className="m-0 font-bold text-xl tracking-wide">
                💰 Transaction Details
              </h3>
              <button
                onClick={() => setShowTransactionModal(false)}
                className="bg-white bg-opacity-20 hover:bg-opacity-30 border-none text-white text-xl cursor-pointer p-2 w-10 h-10 flex items-center justify-center rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50"
              >
                ×
              </button>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                  <span className="font-semibold text-gray-600">Type:</span>
                  <span
                    className={`font-bold px-3 py-1 rounded-full text-sm ${
                      selectedTransaction.type === "credit"
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {selectedTransaction.type === "credit"
                      ? "+ Received"
                      : "- Sent"}
                  </span>
                </div>

                <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                  <span className="font-semibold text-gray-600">Amount:</span>
                  <span
                    className={`font-bold text-xl ${
                      selectedTransaction.type === "credit"
                        ? "text-green-600"
                        : "text-red-600"
                    }`}
                  >
                    {selectedTransaction.type === "credit" ? "+" : "-"}$
                    {selectedTransaction.amount.toFixed(2)}
                  </span>
                </div>

                <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                  <span className="font-semibold text-gray-600">
                    {selectedTransaction.other_party_type}:
                  </span>
                  <div className="text-right">
                    <span className="font-bold text-gray-800 block">
                      {selectedTransaction.other_party}
                    </span>
                    {selectedTransaction.other_party_wallet &&
                      selectedTransaction.other_party_wallet !== "N/A" && (
                        <span className="font-medium text-blue-600 text-sm">
                          💳 {selectedTransaction.other_party_wallet}
                        </span>
                      )}
                  </div>
                </div>

                {selectedTransaction.transaction_number && (
                  <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                    <span className="font-semibold text-gray-600">
                      Transaction ID:
                    </span>
                    <span className="font-mono text-gray-800">
                      {selectedTransaction.transaction_number}
                    </span>
                  </div>
                )}

                <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                  <span className="font-semibold text-gray-600">
                    Date & Time:
                  </span>
                  <span className="text-gray-800">
                    {formatDate(selectedTransaction.created_at)} at{" "}
                    {formatTime(selectedTransaction.created_at)}
                  </span>
                </div>

                <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                  <span className="font-semibold text-gray-600">
                    Balance Before:
                  </span>
                  <span className="font-mono text-gray-800">
                    ${selectedTransaction.prev_balance.toFixed(2)}
                  </span>
                </div>

                <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                  <span className="font-semibold text-gray-600">
                    Balance After:
                  </span>
                  <span className="font-mono text-gray-800">
                    ${selectedTransaction.new_balance.toFixed(2)}
                  </span>
                </div>

                {selectedTransaction.description && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <span className="font-semibold text-gray-600 block mb-2">
                      Description:
                    </span>
                    <span className="text-gray-800">
                      {selectedTransaction.description}
                    </span>
                  </div>
                )}
              </div>

              <div className="flex justify-end mt-6 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setShowTransactionModal(false)}
                  className="px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-xl font-semibold hover:from-purple-700 hover:to-purple-800 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-purple-300"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Modal for Profile Picture */}
      <ImageModal
        images={profileImage || ProfilePic}
        initialIndex={0}
        isOpen={isImageModalOpen}
        onClose={() => setIsImageModalOpen(false)}
        alt="Profile Picture"
        isBase64={false}
      />
    </div>
  );
};

export default ManageUser;
